import { ArrowBack, ArrowForward, Delete } from "@mui/icons-material";
import { Box, Dialog, Grid2 } from "@mui/material";
import { PimoClassComponent, PimoReactComponent } from "@pimo/pimo-app-builder";
import { FC, useEffect, useRef, useState } from "react";
import {
  DefaultValues,
  FieldError,
  FieldErrors,
  FieldValues,
  FormProvider,
  get,
  Path,
  PathValue,
  useForm,
  UseFormRegister,
  UseFormReturn,
  UseFormSetValue,
} from "react-hook-form";

import {
  ATTACHMENT_FORM_FIELD_TYPE,
  AttachmentFormField,
} from "./fields/attachment-form-field";
import {
  CHECKBOX_FORM_FIELD_TYPE,
  CheckBoxFormField,
} from "./fields/checkbox-form-field";
import { DATE_FORM_FIELD_TYPE, DateFormField } from "./fields/date-form-field";
import {
  DROPDOWN_FORM_FIELD_TYPE,
  DropdownFormField,
} from "./fields/dropdown-form-field";
import {
  MULTI_TEXT_FORM_FIELD_TYPE,
  MultiTextFormField,
} from "./fields/multi-text-form-field/multi-text-form-field";
import {
  NUMBER_FORM_FIELD_TYPE,
  NumberFormField,
} from "./fields/number-form-field";
import {
  RADIO_FORM_FIELD_TYPE,
  RadioFormField,
} from "./fields/radio-form-field";
import {
  RICH_TEXT_FORM_FIELD_TYPE,
  RichTextFormField,
} from "./fields/rich-text-field";
import { TEXT_FORM_FIELD_TYPE, TextFormField } from "./fields/text-form-field";
import type {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
  CustomFieldComponent,
  FormFieldDefinition,
} from "./form-field";
import { FormFieldWrapper } from "./form-field-wrapper";
import { FormTab, FormTabDefinition, TabCompletionStatus } from "./form-tab";
import type {
  BaseLayoutElementDefinition,
  CustomLayoutElementComponent,
  LayoutElementDefinition,
} from "./layout-element";
import {
  FORM_LABEL_LAYOUT_ELEMENT_TYPE,
  FormLabelLayoutElement,
} from "./layout-elements/form-label";
import {
  FORM_TAB_TITLE_LAYOUT_ELEMENT_TYPE,
  FormTabTitleLayoutElement,
} from "./layout-elements/form-tab-title";
import {
  SUBHEADLINE_LAYOUT_ELEMENT_TYPE,
  SubheadlineLayoutElement,
} from "./layout-elements/subheadline";
import { LayoutFieldWrapper } from "./layout-field-wrapper";
import {
  FormNavigationAction,
  FormNavigationActionConfig,
  FormNavigationWrapper,
  FormNavigationWrapperProps,
} from "./navigation/navigation-wrapper";

export type DeriveFCProps<Component> =
  Component extends FC<infer Props> ? Props : never;

const DEFAULT_FORM_FIELDS = {
  [TEXT_FORM_FIELD_TYPE]: TextFormField,
  [MULTI_TEXT_FORM_FIELD_TYPE]: MultiTextFormField,
  [NUMBER_FORM_FIELD_TYPE]: NumberFormField,
  [CHECKBOX_FORM_FIELD_TYPE]: CheckBoxFormField,
  [DROPDOWN_FORM_FIELD_TYPE]: DropdownFormField,
  [DATE_FORM_FIELD_TYPE]: DateFormField,
  [ATTACHMENT_FORM_FIELD_TYPE]: AttachmentFormField,
  [RADIO_FORM_FIELD_TYPE]: RadioFormField,
  [RICH_TEXT_FORM_FIELD_TYPE]: RichTextFormField,
} as const satisfies {
  [K in FormFieldDefinition["type"]]: FC<
    Extract<FormFieldDefinition & AdditionalFormFieldProps, { type: K }>
  >;
};

const DEFAULT_LAYOUT_ELEMENTS = {
  [SUBHEADLINE_LAYOUT_ELEMENT_TYPE]: SubheadlineLayoutElement,
  [FORM_LABEL_LAYOUT_ELEMENT_TYPE]: FormLabelLayoutElement,
  [FORM_TAB_TITLE_LAYOUT_ELEMENT_TYPE]: FormTabTitleLayoutElement,
} as const satisfies {
  [K in LayoutElementDefinition["type"]]: FC<
    Extract<LayoutElementDefinition, { type: K }>
  >;
};

export type DefaultFormFields = typeof DEFAULT_FORM_FIELDS;
export type DefaultLayoutElements = typeof DEFAULT_LAYOUT_ELEMENTS;
export type CustomFormFields<
  Type extends string = never,
  Props extends BaseFormFieldDefinition<Type> & AdditionalFormFieldProps =
    never,
> = Record<Type, CustomFieldComponent<Props>>;
export type CustomLayoutElements<
  Type extends string = never,
  Props extends BaseLayoutElementDefinition<Type> = never,
> = Record<Type, CustomLayoutElementComponent<Props>>;

export type PimoFormEvent =
  | "delete"
  | "cancel"
  | "cancel-with-changes"
  | "close"
  | "submit"
  | "submit-without-changes"
  /** The `submit` action refused to finalize because validation failed. */
  | "submit-blocked"
  | "navigate-with-changes"
  | "navigate"
  | "confirm-up-to-date"
  | "show-alert-dialog";

export type ValidateFormData = (
  formData: Record<string, unknown>
) => Record<Path<FieldValues>, FieldError>;

export type ValidationTarget = "all-tabs" | "current-tab" | "none";

export type AdditionalFormProps = {
  actions?: {
    [key in FormNavigationAction]?: Partial<FormNavigationActionConfig>;
  };
  /** `true` saves on both Next and Back; the object form saves only on the listed directions. */
  saveOnAction?: boolean | { next?: boolean; back?: boolean };
  validationTarget?: ValidationTarget;
  validateFormData?: ValidateFormData;
  runValidationOn?: "change" | "blur";
  useWatchForEntireForm?: boolean;
  isCheckBoxChecked?: boolean;
  addCheckBoxToForm?: boolean;
  alertDialog?: React.FC;
  saveAndCloseLabel?: string;
  title?: string;
  initialSelectedTabId?: string;
  /** Called whenever the active tab changes (sidebar click, Next, or Back). */
  onTabChange?: (tabId: string) => void;
  /** if set to `true`, the tabs navigation is not displayed and the form is not artifically pulled to 100% screen width */
  inline?: boolean;
  enableCloseButton?: boolean;
  expandAllChildren?: boolean;
  /** if set to `true`, each tab title shows a completion indicator (not started / in progress / complete) */
  showTabCompletionIndicator?: boolean;
  /** if set to `true`, the `submit` action stays disabled until every navigable tab is complete */
  disableSubmitUntilComplete?: boolean;
  /**
   * if set to `true`, Back and Save & Next are disabled when no navigable tab lies
   * that way. Without it they only follow the plain tab index, which leaves the
   * button enabled but inert when the neighbouring tab is empty or hidden.
   */
  disableNavigationWithoutTarget?: boolean;
  /** if set to `true`, Save stays disabled until a field has actually been edited */
  disableSaveWithoutEdits?: boolean;
  /** if set to `true`, tabs are only shown as complete after the user has opened them at least once */
  showCompleteOnlyAfterTabVisit?: boolean;
} & Pick<FormNavigationWrapperProps, "navigationSlot" | "titleBadge">;

export type FormNavigationDirection = "back" | "next" | "none";

export class PimoForm<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormValues extends Record<string, any>,
  CustomFormFieldDefinitions extends CustomFormFields = never,
  CustomLayoutElementDefinitions extends CustomLayoutElements = never,
> implements PimoClassComponent<
  FormValues & AdditionalFormProps,
  PimoFormEvent,
  FormValues
> {
  private setFormTabsState?: React.Dispatch<
    React.SetStateAction<
      FormTab<
        FormValues,
        CustomFormFieldDefinitions,
        CustomLayoutElementDefinitions
      >[]
    >
  >;

  initializeFormTabsState(
    setState: React.Dispatch<
      React.SetStateAction<
        FormTab<
          FormValues,
          CustomFormFieldDefinitions,
          CustomLayoutElementDefinitions
        >[]
      >
    >
  ) {
    this.setFormTabsState = setState;
  }
  #availableFormFields = {
    ...DEFAULT_FORM_FIELDS,
  } as DefaultFormFields & CustomFormFieldDefinitions;

  #availableLayoutElements = {
    ...DEFAULT_LAYOUT_ELEMENTS,
  } as DefaultLayoutElements & CustomLayoutElementDefinitions;

  #flatFormTabsInOrderOfNavigation: FormTab<
    FormValues,
    CustomFormFieldDefinitions,
    CustomLayoutElementDefinitions
  >[] = [];

  #lastActionAttempted: "save-and-close" | "save-and-next" | "none" | null =
    null;

  #setFormValues?: UseFormSetValue<FormValues>;

  setFormValue(
    key: Path<FormValues>,
    value: PathValue<FormValues, Path<FormValues>>
  ) {
    this.#setFormValues?.(key, value, { shouldDirty: true });
  }

  getLastActionAttempted() {
    return this.#lastActionAttempted;
  }

  #formMethods?: UseFormReturn<FormValues> & {
    areAllTabsValid: () => Promise<boolean>;
  };

  #formTabs: FormTab<
    FormValues,
    CustomFormFieldDefinitions,
    CustomLayoutElementDefinitions
  >[] = [];

  #title?: string;

  #navigateCallback?: (direction: FormNavigationDirection) => void;

  public navigate(direction: FormNavigationDirection) {
    this.#navigateCallback?.(direction);
  }

  addFormTab(
    definition: FormTabDefinition
  ): FormTab<
    FormValues,
    CustomFormFieldDefinitions,
    CustomLayoutElementDefinitions
  > {
    const tab = new FormTab<
      FormValues,
      CustomFormFieldDefinitions,
      CustomLayoutElementDefinitions
    >(definition, () => this.#buildFlatFormTabs());

    this.#formTabs.push(tab);
    this.setFormTabsState?.([...this.#formTabs]);
    this.#buildFlatFormTabs();
    return tab;
  }

  setTitle(title: string) {
    this.#title = title;
  }

  /**
   * Builds a flat list of form tabs in the order they should be navigated through.
   *
   * The reason we need this is that `_formTabs` contains a tree structure of tabs and child tabs:
   *
   * - tab a
   *   - tab a.1
   *   - tab a.2
   *
   * - tab b
   *
   * However, for navigating through tabs, we need a flat list (tab a => tab a.1 => tab a.2 => tab b.)
   */
  #buildFlatFormTabs() {
    const flatTabs: FormTab<
      FormValues,
      CustomFormFieldDefinitions,
      CustomLayoutElementDefinitions
    >[] = [];

    function addFlattenedTabs(
      tab: FormTab<
        FormValues,
        CustomFormFieldDefinitions,
        CustomLayoutElementDefinitions
      >
    ) {
      flatTabs.push(tab);

      for (const childTab of tab.childTabs) {
        addFlattenedTabs(childTab);
      }
    }

    for (const tab of this.#formTabs) {
      addFlattenedTabs(tab);
    }

    this.#flatFormTabsInOrderOfNavigation = flatTabs;
  }

  getFormMethods = () => {
    return this.#formMethods;
  };

  async buildDynamicTabsAsynchronously(
    buildFunction: (form: this) => Promise<void>
  ): Promise<void> {
    this.#formTabs = [];
    await buildFunction(this);
  }

  render(): PimoReactComponent<
    FormValues & AdditionalFormProps,
    PimoFormEvent,
    FormValues
  > {
    return ({
      actions,
      fireEvent,
      inDialog,
      navigationSlot,
      saveOnAction,
      validateFormData,
      validationTarget = "current-tab",
      runValidationOn = "change",
      isCheckBoxChecked,
      addCheckBoxToForm = false,
      isCheckBoxDisabled = false,
      useWatchForEntireForm = false,
      saveAndCloseText = "Save & Close",
      disabledSaveAndCloseButton = false,
      disabledSaveAndNextButton = false,
      saveAndNextText = "Save & Next",
      alertDialog,
      title,
      titleBadge,
      inline,
      enableCloseButton,
      expandAllChildren,
      showTabCompletionIndicator = false,
      disableSubmitUntilComplete = false,
      disableNavigationWithoutTarget = false,
      disableSaveWithoutEdits = false,
      showCompleteOnlyAfterTabVisit = false,
      onTabChange,
      initialSelectedTabId,
      ...formData
    }) => {
      if (this.isEmptyOrAllUndefinedZero(formData)) {
        return <></>;
      }
      const [selectedTab, setSelectedTab] = useState(
        initialSelectedTabId ?? this.#formTabs[0]?.id
      );
      const [visitedTabIds, setVisitedTabIds] = useState<Set<string>>(
        new Set(
          [initialSelectedTabId ?? this.#formTabs[0]?.id].filter(
            (tabId): tabId is string => Boolean(tabId)
          )
        )
      );
      const [tabs, setTabs] = useState<
        FormTab<
          FormValues,
          CustomFormFieldDefinitions,
          CustomLayoutElementDefinitions
        >[]
      >(this.#formTabs);

      const AlertDialog = alertDialog;

      this.initializeFormTabsState(setTabs);

      const methods = useForm<FormValues>({
        defaultValues: (formData ?? undefined) as DefaultValues<FormValues>,
        mode: runValidationOn === "change" ? "all" : "onBlur",
      });
      const validationErrorKeys = useRef<Set<Path<FormValues>>>(new Set());

      this.#setFormValues = methods.setValue;

      const { handleSubmit, formState } = methods;
      const { dirtyFields, touchedFields } = formState;
      const hasChanges = Boolean(
        Object.keys(dirtyFields).length || Object.keys(touchedFields).length
      );
      // Save is about persisting edits, so a merely touched field does not count.
      const hasEdits = Object.keys(dirtyFields).length > 0;

      const onCancel = () => {
        hasChanges ? fireEvent?.("cancel-with-changes") : fireEvent?.("cancel");
      };

      const onCheckBoxClick = () => {
        fireEvent?.("confirm-up-to-date");
      };

      const validateBeforeSave = async (
        overrideValidationTarget?: ValidationTarget
      ): Promise<boolean> => {
        switch (overrideValidationTarget ?? validationTarget) {
          case "all-tabs":
            if (!(await areAllTabsValid())) {
              return false;
            }
            break;
          case "current-tab":
            if (!(await isCurrentTabValid())) {
              alertDialog && fireEvent?.("show-alert-dialog");
              return false;
            }
            break;
          default:
            break;
        }
        return true;
      };

      const navigate = (direction: "next" | "back") => {
        const differenceForDirection = direction === "next" ? 1 : -1;

        const currentTabIndex = this.#flatFormTabsInOrderOfNavigation.findIndex(
          (tab) => tab.id === selectedTab
        );

        // find next tab with form elements, skip over tabs without elements or
        // whose isVisible condition is not currently met
        let newTabIndex = currentTabIndex + differenceForDirection;
        let newTab = this.#flatFormTabsInOrderOfNavigation[newTabIndex];

        while (
          newTab &&
          (newTab.formFields.length === 0 || !isTabVisible(newTab))
        ) {
          newTabIndex += differenceForDirection;
          newTab = this.#flatFormTabsInOrderOfNavigation[newTabIndex];
        }

        if (newTab == null) {
          return;
        }

        setSelectedTab(newTab.id);
        setVisitedTabIds((prev) => {
          const next = new Set(prev);
          next.add(newTab.id);
          return next;
        });
        onTabChange?.(newTab.id);
      };

      const navigateToTab = (id: string) => {
        let newTab = this.#flatFormTabsInOrderOfNavigation.find(
          (tab) => tab.id === id
        );

        if (newTab?.formFields.length === 0 && newTab.childTabs.length > 0) {
          newTab = newTab.childTabs[0];
        }

        if (!newTab || !isTabVisible(newTab)) {
          return;
        }

        setSelectedTab(newTab.id);
        setVisitedTabIds((prev) => {
          const next = new Set(prev);
          next.add(newTab.id);
          return next;
        });
        onTabChange?.(newTab.id);
      };

      const onSave = async (
        data: FormValues,
        overrideValidationTarget?: ValidationTarget
      ) => {
        if (!(await validateBeforeSave(overrideValidationTarget))) {
          return;
        }

        hasChanges
          ? fireEvent?.("submit", data)
          : fireEvent?.("submit-without-changes", data);
      };

      const savesOnNavigation = (direction: "next" | "back") =>
        typeof saveOnAction === "object" && saveOnAction !== null
          ? Boolean(saveOnAction[direction])
          : Boolean(saveOnAction);

      const onSaveAndNext = async (
        data: FormValues,
        overrideValidationTarget?: ValidationTarget
      ) => {
        if (!(await validateBeforeSave(overrideValidationTarget))) {
          return;
        }

        if (savesOnNavigation("next")) {
          await (hasChanges
            ? fireEvent?.("submit", {
                ...data,
                navigationDirection: "next",
              })
            : fireEvent?.("submit-without-changes", {
                ...data,
                navigationDirection: "next",
              }));
        }

        navigate("next");
      };

      const onDelete = () => {
        fireEvent?.("delete");
      };

      const onSaveAndBack = async (
        data: FormValues,
        overrideValidationTarget?: ValidationTarget
      ) => {
        if (!(await validateBeforeSave(overrideValidationTarget))) {
          return;
        }

        if (savesOnNavigation("back")) {
          await (hasChanges
            ? fireEvent?.("submit", {
                ...data,
                navigationDirection: "back",
              })
            : fireEvent?.("submit-without-changes", {
                ...data,
                navigationDirection: "back",
              }));
        }

        navigate("back");
      };

      const onSaveAndClose = async (
        data: FormValues,
        overrideValidationTarget?: ValidationTarget
      ) => {
        if (!(await validateBeforeSave(overrideValidationTarget))) {
          return;
        }

        hasChanges
          ? fireEvent?.("submit", {
              ...data,
            })
          : fireEvent?.("submit-without-changes", {
              ...data,
            });

        fireEvent?.("close");
      };

      // `formState` in this closure is a render-time snapshot, so an error that
      // `trigger` just raised is not visible on it. Live field state is read from
      // the form instead, and only for the `submit` action, so the pre-existing
      // validation paths keep the behaviour their forms are built around.
      const tabsFailingFieldRules = (): Set<string> => {
        const failing = new Set<string>();

        for (const tab of this.#flatFormTabsInOrderOfNavigation) {
          if (!isTabVisible(tab)) continue;

          for (const field of tab.formFields) {
            if (!this.#isFormFieldDefinition(field)) continue;
            if (field.isVisible && !field.isVisible(fieldValues)) continue;
            if (methods.getFieldState(field.name as Path<FormValues>).error) {
              failing.add(tab.id);
            }
          }
        }

        return failing;
      };

      // Finalizing differs from save-and-close in that a failed validation is
      // reported back to the consumer, so it can show its own overarching error
      // instead of the click looking like it did nothing.
      const onFinalSubmit = async (
        data: FormValues,
        overrideValidationTarget?: ValidationTarget
      ) => {
        // This runs first because it sets `hasErrors` per tab from the consumer's
        // own errors; the field-rule markers are then added on top of it.
        const consumerValidationPassed = await validateBeforeSave(
          overrideValidationTarget
        );

        const fieldRulesValid = await methods.trigger();
        const failingTabs = tabsFailingFieldRules();
        for (const tab of this.#flatFormTabsInOrderOfNavigation) {
          if (failingTabs.has(tab.id)) tab.hasErrors = true;
        }

        if (!fieldRulesValid || !consumerValidationPassed) {
          fireEvent?.("submit-blocked");
          return;
        }

        hasChanges
          ? fireEvent?.("submit", { ...data })
          : fireEvent?.("submit-without-changes", { ...data });

        fireEvent?.("close");
      };

      const getFormErrors = async () => {
        const data = methods.getValues();
        const errors = validateFormData?.(data) as Record<
          Path<FieldValues>,
          FieldError
        >;
        const nextValidationErrorKeys = new Set<Path<FormValues>>();

        for (const [key, value] of Object.entries(errors ?? {})) {
          const fieldPath = key as Path<FormValues>;
          methods.setError(fieldPath, value);
          nextValidationErrorKeys.add(fieldPath);
        }

        for (const previousKey of validationErrorKeys.current) {
          if (!nextValidationErrorKeys.has(previousKey)) {
            methods.clearErrors(previousKey);
          }
        }

        validationErrorKeys.current = nextValidationErrorKeys;

        await methods.trigger();

        return errors ?? {};
      };

      const isCurrentTabValid = async () => {
        const currentTab = this.#flatFormTabsInOrderOfNavigation.find(
          (tab) => tab.id === selectedTab
        );

        return isTabValid(currentTab, await getFormErrors());
      };

      const areAllTabsValid = async () => {
        let valid = true;
        const errors = await getFormErrors();

        this.#flatFormTabsInOrderOfNavigation.forEach((tab) => {
          // A hidden tab's precondition is not met, so it is not applicable and
          // cannot block save on account of its own (unseen) fields.
          if (!isTabVisible(tab)) return;
          if (!isTabValid(tab, errors)) {
            valid = false;
          }
        });

        return valid;
      };

      const isTabValid = (
        tab:
          | FormTab<
              FormValues,
              CustomFormFieldDefinitions,
              CustomLayoutElementDefinitions
            >
          | undefined,
        errors: FieldErrors<FieldValues>
      ) => {
        if (!tab) {
          return true;
        }

        let isValid = true;

        for (const field of tab?.formFields ?? []) {
          if (!this.#isFormFieldDefinition(field)) {
            continue;
          }

          const error = get(errors, field.name) as Record<
            Path<FieldValues>,
            FieldError
          >;
          const fieldError = methods.getFieldState(
            field.name as Path<FormValues>
          ).error;

          if (fieldError?.message || error?.message) {
            methods.setError(
              field.name as Path<FormValues>,
              (fieldError ?? error) as FieldError
            );
            isValid = false;
          }
        }

        tab.hasErrors = !isValid;
        return isValid;
      };

      this.#formMethods = {
        areAllTabsValid,
        ...methods,
      };

      this.#navigateCallback = (direction: FormNavigationDirection) => {
        if (direction === "none") return;
        navigate(direction);
      };

      const fieldValues: FormValues = useWatchForEntireForm
        ? methods.watch()
        : methods.getValues();

      const isTabVisible = (
        tab: FormTab<
          FormValues,
          CustomFormFieldDefinitions,
          CustomLayoutElementDefinitions
        >
      ) => !tab.isVisible || tab.isVisible(fieldValues);

      // Same skip rule as `navigate`: a tab without fields, or one whose
      // `isVisible` condition is not met, is not a navigation target.
      const isNavigable = (
        tab: FormTab<
          FormValues,
          CustomFormFieldDefinitions,
          CustomLayoutElementDefinitions
        >
      ) => tab.formFields.length > 0 && isTabVisible(tab);

      const hasNavigableTab = (step: 1 | -1) => {
        const tabs = this.#flatFormTabsInOrderOfNavigation;
        const from = tabs.findIndex((tab) => tab.id === selectedTab);

        for (let i = from + step; i >= 0 && i < tabs.length; i += step) {
          if (isNavigable(tabs[i])) return true;
        }

        return false;
      };

      const allNavigableTabsComplete = disableSubmitUntilComplete
        ? this.#flatFormTabsInOrderOfNavigation
            .filter(isNavigable)
            .every(
              (tab) =>
                this.#computeTabCompletionStatus(tab, fieldValues) ===
                "complete"
            )
        : true;

      const activeTabIndex = this.#formTabs.findIndex(
        ({ id }) => id === selectedTab
      );
      const leftActions = [
        {
          disabled: disableNavigationWithoutTarget
            ? !hasNavigableTab(-1)
            : activeTabIndex === 0,
          key: "save-and-back",
          label: "Back",
          onClick: () => onSaveAndBack(methods.getValues()),
          startIcon: <ArrowBack />,
          variant: "text",
          visible: true,
          ...(actions?.["save-and-back"] ?? {}),
        },

        {
          key: "delete",
          label: "Delete",
          visible: false,
          disabled: false,
          variant: "contained",
          color: "error",
          startIcon: <Delete />,
          onClick: onDelete,
          ...(actions?.["delete"] ?? {}),
        },
      ] as FormNavigationActionConfig[];

      const rightActions = [
        ...(addCheckBoxToForm
          ? [
              {
                key: "confirm-up-to-date",
                isCheckBoxChecked,
                type: "checkbox",
                isCheckBoxDisabled: isCheckBoxDisabled ?? false,
                label:
                  "By clicking SAVE & CLOSE I confirm that the data is up to date",
                onClick: () => onCheckBoxClick(),
                visible: true,
                position: 0,
              },
            ]
          : []),
        {
          key: "cancel",
          label: "Cancel",
          onClick: () => onCancel(),
          variant: "text",
          visible: true,

          ...(actions?.["cancel"] ?? {}),
        },
        {
          key: "save",
          label: "Save",
          ...(disableSaveWithoutEdits ? { disabled: !hasEdits } : {}),
          onClick: () =>
            onSave(
              methods.getValues(),
              actions?.["save"]?.overrideValidationTarget
            ),
          variant: "text",
          visible: false,
          ...(actions?.["save"] ?? {}),
        },
        {
          key: "save-and-close",
          label: saveAndCloseText,
          disabled: disabledSaveAndCloseButton,
          onClick: () => {
            onSaveAndClose(
              methods.getValues(),
              actions?.["save-and-close"]?.overrideValidationTarget
            );
            this.#lastActionAttempted = "save-and-close";
          },
          variant:
            activeTabIndex === this.#formTabs.length - 1 ? "contained" : "text",
          visible: true,
          ...(actions?.["save-and-close"] ?? {}),
        },
        {
          key: "submit",
          label: "Submit",
          // Opt-in: a form that does not configure this action never renders it.
          visible: false,
          variant: "contained",
          onClick: () => {
            onFinalSubmit(
              methods.getValues(),
              actions?.["submit"]?.overrideValidationTarget ?? "all-tabs"
            );
            this.#lastActionAttempted = "save-and-close";
          },
          ...(actions?.["submit"] ?? {}),
          // Completeness is only known in here, so it is ORed onto whatever the
          // caller asked for instead of being overwritten by it.
          disabled:
            (actions?.["submit"]?.disabled ?? false) ||
            !allNavigableTabsComplete,
        },
        {
          endIcon: <ArrowForward />,
          key: "save-and-next",
          disabled:
            disabledSaveAndNextButton ||
            (disableNavigationWithoutTarget && !hasNavigableTab(1)),
          label: saveAndNextText,
          onClick: () => {
            onSaveAndNext(
              methods.getValues(),
              actions?.["save-and-next"]?.overrideValidationTarget
            );
            this.#lastActionAttempted = "save-and-next";
          },
          variant: "contained",
          visible: (activeTabIndex ?? 0) <= this.#formTabs.length - 2,
          ...(actions?.["save-and-next"] ?? {}),
        },
      ]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .filter(Boolean) as FormNavigationActionConfig[];

      // Move actions to left side if they have slot: "left"
      let finalLeftActions: FormNavigationActionConfig[] = [...leftActions];
      let finalRightActions: FormNavigationActionConfig[] = [...rightActions];

      const saveAndNextConfig = actions?.["save-and-next"];
      if (saveAndNextConfig?.slot === "left") {
        const saveAndNextAction = finalRightActions.find(
          (a) => a.key === "save-and-next"
        );
        if (saveAndNextAction) {
          finalRightActions = finalRightActions.filter(
            (a) => a.key !== "save-and-next"
          );
          const backButtonIndex = finalLeftActions.findIndex(
            (a) => a.key === "save-and-back"
          );
          if (backButtonIndex >= 0) {
            finalLeftActions = [
              ...finalLeftActions.slice(0, backButtonIndex + 1),
              saveAndNextAction,
              ...finalLeftActions.slice(backButtonIndex + 1),
            ];
          }
        }
      }

      // Tabs are frequently built asynchronously (fetched config, dynamic blocks, …),
      // so `selectedTab`'s one-shot useState initializer can lock in a value from
      // before any tabs existed. Once real (visible) tabs land — or an isVisible
      // condition flips and hides the currently selected one — snap to a valid
      // tab: the requested id if it is visible, otherwise the first visible one.
      useEffect(() => {
        const visibleTabIds = tabs
          .flatMap((tab) => [tab, ...tab.childTabs])
          .filter(isTabVisible)
          .map((tab) => tab.id);
        if (visibleTabIds.length === 0) return;

        setSelectedTab((current) => {
          if (visibleTabIds.includes(current)) return current;
          return visibleTabIds.includes(initialSelectedTabId ?? "")
            ? (initialSelectedTabId as string)
            : visibleTabIds[0];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [tabs, fieldValues]);

      useEffect(() => {
        if (!selectedTab) return;
        setVisitedTabIds((prev) => {
          if (prev.has(selectedTab)) return prev;
          const next = new Set(prev);
          next.add(selectedTab);
          return next;
        });
      }, [selectedTab]);

      if (showTabCompletionIndicator) {
        for (const tab of this.#flatFormTabsInOrderOfNavigation) {
          // A tab whose precondition is not met is not applicable, so it is
          // vacuously "complete" rather than counting as unfinished — it just
          // never shows a completion indicator, since it is hidden entirely.
          const visible = isTabVisible(tab);
          const completionStatus = visible
            ? this.#computeTabCompletionStatus(tab, fieldValues)
            : "complete";

          tab.completionStatus =
            visible &&
            showCompleteOnlyAfterTabVisit &&
            completionStatus === "complete" &&
            !visitedTabIds.has(tab.id)
              ? "in-progress"
              : completionStatus;
        }
      }

      const renderField = (
        field: FormTab<
          FormValues,
          CustomFormFieldDefinitions,
          CustomLayoutElementDefinitions
        >["formFields"][number],
        fieldIndex: number,
        tabIndex: number
      ) => {
        if (this.#isFormFieldDefinition(field)) {
          if (field.isVisible && !field.isVisible(fieldValues)) {
            return undefined;
          }

          const ElementForField = this.#getElementForFormFieldByType(
            field.type as
              keyof DefaultFormFields | keyof CustomFormFieldDefinitions
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ) as FC<any>;

          if (!ElementForField) {
            return undefined;
          }

          const errorMessage = get(formState.errors, field.name)?.message as
            string | undefined;

          return (
            <FormFieldWrapper
              description={field.description}
              infoSlot={field.infoSlot}
              key={`field-${fieldIndex}-field.${field.name}}`}
              label={field.type !== "checkbox" ? field.label : undefined}
              uiOptions={field.uiOptions}
            >
              <ElementForField
                {...field}
                register={methods.register as UseFormRegister<FieldValues>}
                errorMessage={errorMessage}
                value={
                  fieldValues?.[
                    field.name as unknown as keyof typeof fieldValues
                  ]
                }
                formData={{ ...formData, ...fieldValues }}
                getElementForFormFieldByType={(
                  type:
                    keyof DefaultFormFields | keyof CustomFormFieldDefinitions
                ) => this.#getElementForFormFieldByType(type)}
              />
            </FormFieldWrapper>
          );
        } else if (this.#isLayoutElementDefinition(field)) {
          if (field.isVisible && !field.isVisible(fieldValues)) {
            return undefined;
          }
          const ElementForField = this.#getElementForLayoutElementByType(
            field.type
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ) as unknown as FC<any>;

          if (!ElementForField) {
            return undefined;
          }

          return (
            <LayoutFieldWrapper {...field} key={`${tabIndex}_${fieldIndex}`}>
              <ElementForField {...field} />
            </LayoutFieldWrapper>
          );
        }
        return undefined;
      };

      return (
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit((data) => onSaveAndNext(data))}
            style={{ height: "100%" }}
          >
            {
              <FormNavigationWrapper
                actions={[finalLeftActions, finalRightActions]}
                activeTab={selectedTab}
                inDialog={!!inDialog}
                navigationSlot={navigationSlot}
                onTabSelected={(tab) => navigateToTab(tab)}
                tabs={tabs}
                title={title || this.#title}
                titleBadge={titleBadge}
                inline={inline}
                onClose={() => onCancel()}
                enableCloseButton={enableCloseButton}
                expandAllChildren={expandAllChildren}
                isTabVisible={isTabVisible}
              >
                {tabs
                  .flatMap((tab) => [tab, ...tab.childTabs])
                  .filter(isTabVisible)
                  .map((tab, i) => {
                    const pinnedFields = tab.formFields.filter(
                      (field) =>
                        this.#isFormFieldDefinition(field) &&
                        field.uiOptions?.pinnedToBottom
                    );
                    const regularFields = tab.formFields.filter(
                      (field) => !pinnedFields.includes(field)
                    );

                    return (
                      <Box
                        key={i}
                        sx={{
                          display: selectedTab === tab.id ? "flex" : "none",
                          flexDirection: "column",
                          flex: "1 1 auto",
                        }}
                      >
                        <Grid2 container spacing={4} alignItems="stretch">
                          {regularFields.map((field, fieldIndex) =>
                            renderField(field, fieldIndex, i)
                          )}
                        </Grid2>
                        {pinnedFields.length > 0 && (
                          <Box sx={{ mt: "auto", pt: 8 }}>
                            <Grid2 container spacing={4}>
                              {pinnedFields.map((field, fieldIndex) =>
                                renderField(field, fieldIndex, i)
                              )}
                            </Grid2>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
              </FormNavigationWrapper>
            }

            <>{AlertDialog && <AlertDialog />}</>
          </form>
        </FormProvider>
      );
    };
  }

  #getElementForFormFieldByType(
    type: keyof DefaultFormFields | keyof CustomFormFieldDefinitions
  ) {
    return this.#availableFormFields[type];
  }

  #getElementForLayoutElementByType(
    type: keyof DefaultLayoutElements | keyof CustomLayoutElementDefinitions
  ) {
    return this.#availableLayoutElements[type];
  }

  #isFormFieldDefinition(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element: any
  ): element is BaseFormFieldDefinition<string> {
    return !!(element as BaseFormFieldDefinition<string>).name;
  }

  #computeTabCompletionStatus(
    tab: FormTab<
      FormValues,
      CustomFormFieldDefinitions,
      CustomLayoutElementDefinitions
    >,
    fieldValues: FieldValues
  ): TabCompletionStatus {
    const isFilled = (field: BaseFormFieldDefinition<string>) => {
      const value = get(fieldValues, field.name);
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    };

    const visibleFields = tab.formFields
      .filter((field) => this.#isFormFieldDefinition(field))
      .filter((field) => !field.uiOptions?.excludeFromCompletion)
      .filter(
        (field) => !field.isVisible || field.isVisible(fieldValues)
      ) as BaseFormFieldDefinition<string>[];

    // Nothing to fill in means nothing to wait for, so the tab cannot hold back
    // an action that requires every tab to be complete.
    if (visibleFields.length === 0) {
      return "complete";
    }

    const mandatoryFields = visibleFields.filter(
      (field) => field.required?.value
    );
    const anyFieldFilled = visibleFields.some(isFilled);

    if (mandatoryFields.length > 0) {
      if (mandatoryFields.every(isFilled)) return "complete";
      return anyFieldFilled ? "in-progress" : "not-started";
    }

    return anyFieldFilled ? "complete" : "not-started";
  }

  #isLayoutElementDefinition(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    element: any
  ): element is BaseLayoutElementDefinition {
    return !!(element as BaseLayoutElementDefinition).type;
  }

  registerFormField<
    NameType extends keyof DefaultFormFields | keyof CustomFormFieldDefinitions,
  >(
    type: NameType,
    component: NameType extends keyof DefaultFormFields
      ? DefaultFormFields[NameType]
      : NameType extends keyof CustomFormFieldDefinitions
        ? CustomFormFieldDefinitions[NameType]
        : never
  ) {
    // @ts-expect-error - TypeScript cannot prove this generic index write is sound: `component`'s type is resolved by the conditional type on `NameType`, which it will not relate back to the record's value type.
    this.#availableFormFields[type] = component;
  }

  registerLayoutElement<
    NameType extends
      keyof DefaultLayoutElements | keyof CustomLayoutElementDefinitions,
  >(
    type: NameType,
    component: NameType extends keyof DefaultLayoutElements
      ? DefaultLayoutElements[NameType]
      : NameType extends keyof CustomLayoutElementDefinitions
        ? CustomLayoutElementDefinitions[NameType]
        : never
  ) {
    // @ts-expect-error - TypeScript cannot prove this generic index write is sound: `component`'s type is resolved by the conditional type on `NameType`, which it will not relate back to the record's value type.
    this.#availableLayoutElements[type] = component;
  }
  isEmptyOrAllUndefinedZero(obj: Record<string, unknown>) {
    // If object has no properties, consider it empty.
    if (Object.keys(obj).length === 0) {
      return true;
    }

    // Otherwise, check each value.
    for (const value of Object.values(obj)) {
      // If any value is not `undefined` or `0`, return false.
      if (value != null) {
        return false;
      }
    }

    // If we reach here, all values are `undefined` or `0`.
    return true;
  }
}

/**
 * Wraps the regular Pimo form into a dialog.
 */
export class PimoFormDialog<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FormValues extends Record<string, any>,
  CustomFormFieldDefinitions extends CustomFormFields = never,
  CustomLayoutElementDefinitions extends CustomLayoutElements = never,
> extends PimoForm<
  FormValues,
  CustomFormFieldDefinitions,
  CustomLayoutElementDefinitions
> {
  render(): PimoReactComponent<
    FormValues & {
      dialogStyles?: Record<string, unknown>;
    } & AdditionalFormProps,
    PimoFormEvent,
    FormValues & {
      path?: string;
    }
  > {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SuperPimoForm: React.FC<any> = super.render();

    return ({ fireEvent, ...formData }) => {
      return (
        <Dialog
          open
          fullScreen
          sx={{ ...formData.dialogStyles, m: "5%" }}
          PaperProps={{ sx: { borderRadius: "10px" } }}
        >
          <SuperPimoForm fireEvent={fireEvent} {...formData} inDialog />
        </Dialog>
      );
    };
  }
}
